import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import util from 'util/Util.js';
import listService from 'ListService';

const PanelWrapper = styled.div`
  font-family: 'Poppins', sans-serif; 
  background-color: #ECECEC;
  padding: 20px;
  border-radius: 4px;
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 12px;
  overflow-y: auto;
`;

const TranslationSection = styled.div`
/* Leave for future styling */
`;

const Label = styled.label`
  display: block;
  font-size: 1.2rem;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
`;

const StyledTextArea = styled.textarea`
  width: 100%;
  min-height: 50px;
  padding: 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 1.2rem;
  font-family: inherit;
  box-sizing: border-box;
  resize: vertical;

  &:read-only {
    background-color: #f0f0f0;
    color: #555;
    cursor: default;
  }

  &:focus {
    outline: none;
    border-color: #85B33E;
    box-shadow: 0 0 0 2px rgba(133, 179, 62, 0.2);
  }
`;

const InputGroup = styled.div`
  position: relative;
`;

const IconContainer = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  display: flex;
  gap: 4px;
`;

const IconButton = styled.i`
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  color: #777;
  font-size: 1.2rem;

  &:hover {
    color: #333;
  }

`;

// --- TranslationPanel Component ---
const TranslationPanel = ({ selectedObject, languages = [], currentTranslations, setCurrentTranslations, setIsDirty }) => {

  const [defaultText, setDefaultText] = useState('');
  const NEW_ROW_ID = 'new-language-temp-id';

  useEffect(() => {
    if (selectedObject?.id)  {
      setDefaultText(selectedObject.defaultText || '');

      const objId = listService.getCleanedId(selectedObject.obj) || selectedObject.obj;
      const field = selectedObject.key;

      setCurrentTranslations(prev => {
        const updated = { ...prev };

        if (!updated[objId]) updated[objId] = {};
        if (!updated[objId][field]) updated[objId][field] = {};

        languages.forEach(lang => {
          const langCode = lang.code || lang.id;
          if (!(langCode in updated[objId][field])) {
            updated[objId][field][langCode] =
              selectedObject.translations?.[field]?.[langCode] || '';
          }
        });

        return updated;
      });
    }
  }, [selectedObject, languages]);

  const handleTranslationChange = (langCode, value) => {
    setIsDirty(true);
        
        const objId = listService.getCleanedId(selectedObject.obj) || selectedObject.obj;
        const columnId = selectedObject.columnId ? (listService.getCleanedId(selectedObject.columnId) || selectedObject.columnId) : null;
        const field = selectedObject.key;

        setCurrentTranslations(prev => {
            const newState = { ...prev };

            if (!newState[objId]) newState[objId] = {};

            if (columnId) {
                if (!newState[objId].columns) newState[objId].columns = {};
                if (!newState[objId].columns[columnId]) newState[objId].columns[columnId] = {};
                if (!newState[objId].columns[columnId][field]) newState[objId].columns[columnId][field] = {};
                newState[objId].columns[columnId][field][langCode] = value;
            } else {
                if (!newState[objId][field]) newState[objId][field] = {};
                newState[objId][field][langCode] = value;
            }

            return newState;
        });
  };

  const handleBlur = (langCode) => {

    const objId = listService.getCleanedId(selectedObject.obj) || selectedObject.obj;
    const columnId = selectedObject.columnId ? (listService.getCleanedId(selectedObject.columnId) || selectedObject.columnId) : null;
    const field = selectedObject.key;

    const rawValue = getDisplayText(langCode);

    const sanitizedValue = util.sanitizeHTML(rawValue, true, rawValue, true);

    // Only update state if the sanitization actually changed something.
    if (rawValue !== sanitizedValue) {
         setCurrentTranslations(prev => {
            const newState = { ...prev };

            if (columnId) {
                newState[objId].columns[columnId][field][langCode] = sanitizedValue;
            } else {
                newState[objId][field][langCode] = sanitizedValue;
            }

            return newState;
        });
    }
};

  const handleGoogleTranslate = (textToTranslate, targetLangCode) => {
    if (!textToTranslate) return;
    const cleanLang = targetLangCode.split("-")[0]; //google translate does not use specific locale
    const url = `https://translate.google.com/?sl=auto&tl=${cleanLang}&text=${encodeURIComponent(textToTranslate)}`;
    window.open(url, '_blank');
  };

  const handleCopyToClipboard = async (textToCopy, langName) => {
    if (!textToCopy) return;
    try {
      const textArea = document.createElement("textarea");
      textArea.value = textToCopy;
      textArea.style.position = "fixed";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  if (!selectedObject?.id) {
    return (
      <PanelWrapper>
        <p style={{ textAlign: 'center', color: '#777', marginTop: '20px' }}>
          Select an object from the tree to manage its translations.
        </p>
      </PanelWrapper>
    );
  }

  const getDisplayText = (langCode) => {
    
    const objId = listService.getCleanedId(selectedObject.obj) || selectedObject.obj;
    const columnId = selectedObject.columnId ? (listService.getCleanedId(selectedObject.columnId) || selectedObject.columnId) : null;
    const field = selectedObject.key;

    if (columnId) {
      // Path for a nested column property
      return currentTranslations?.[objId]?.columns?.[columnId]?.[field]?.[langCode] || '';
    } else {
      // Path for a regular property
      return currentTranslations?.[objId]?.[field]?.[langCode] || '';
    }
  };

  return (
    <PanelWrapper>
      {/* Default Text Section */}
      <TranslationSection>
        <Label htmlFor={`default-text-${selectedObject.id}`}>Default</Label>
        <StyledTextArea
          id={`default-text-${selectedObject.id}`}
          value={selectedObject.defaultText}
          readOnly
        />
      </TranslationSection>

      {/* Language specific Translation Sections */}
      {languages.map(lang => {
        const currentText = getDisplayText(lang.code);

        if (lang.code !== NEW_ROW_ID) {
          return (
            <TranslationSection key={lang.code}>
              <Label htmlFor={`translation-${selectedObject.id}-${lang.code}`}>
                {lang.name}
              </Label>
              <InputGroup>
                <StyledTextArea
                  id={`translation-${selectedObject.id}-${lang.code}`}
                  value={currentText}
                  onChange={(e) => handleTranslationChange(lang.code, e.target.value)}
                  onBlur={() => handleBlur(lang.code)} // Sanitizes when focus is lost
                  placeholder={`Enter ${lang.name} translation...`}
                />
                <IconContainer>
                  <IconButton
                    title={`Translate "${selectedObject.defaultText}" to ${lang.name} with Google`}
                    className="pf-icon-gtranslate"
                    onClick={() => handleGoogleTranslate(selectedObject.defaultText, lang.code)}
                  />
                  <IconButton
                    title={`Copy ${lang.name} text`}
                    className="pf-icon-copylink"
                    onClick={() => handleCopyToClipboard(currentText, lang.name)}
                  />
                </IconContainer>
              </InputGroup>
            </TranslationSection>
          );
        }
      })}
    </PanelWrapper>
  );
};
export default TranslationPanel;
